/** @format */

import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import {
	selectSameDayDeliveryButtonText,
	selectShipMode,
	setFulfillmentButtonsShipMode
} from '@pdp/add-to-cart/data-access';
import { selectKillswitch, selectResolvedSku } from '@pdp/data-access/state';

import { Store } from '@ngrx/store';
import { selectUserZipCode } from '@state/data-access';
import { PdpFulfillmentButtonComponent } from '../../ui/fulfillment-button/fulfillment-button.component';
import {
	homefieldIconFulfillmentSamedayFilled,
	homefieldIconFulfillmentSamedayOutlined,
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { ChangeZipCodeButtonComponent } from '../../change-zip-code-button/change-zip-code-button.component';

@Component({
	selector: 'pdp-feature-same-day-delivery-button',
	templateUrl: './same-day-delivery-button.component.html',
	styleUrl: './same-day-delivery-button.component.scss',
	imports: [
		PdpFulfillmentButtonComponent,
		HomefieldIconLibraryModule,
		ChangeZipCodeButtonComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SameDayDeliveryButtonComponent {
	private readonly store = inject(Store);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
	/**
	 * @description state selector returns an object that has the information to display for the fulfillment button and button label
	 */
	sameDayDeliveryButtonText = this.store.selectSignal(selectSameDayDeliveryButtonText);

	/**
	 * @description selects shipMode from fulfillment slice of state.
	 * This is used to determine the selected CSS state of the fulfillment button and
	 * is passed to the analytics event so they know which method has been chosen.
	 */
	shipMode = this.store.selectSignal(selectShipMode);

	skuResolved = this.store.selectSignal(selectResolvedSku);

	userZipCode = this.store.selectSignal(selectUserZipCode);

	/** @description Determine if the updated sdd CutOff Time is enabled */
	sddUpdatedCutoffEnabled = this.store.selectSignal(selectKillswitch('sddUpdatedCutoffEnabled'));

	fulfillmentIconName = computed(() => {
		const buttonEnabled =
			!this.sameDayDeliveryButtonText().fulfillmentButtonDisabledClass ||
			this.isZipcodeNotSetAndSkuResolved();
		return this.shipMode() == 'sameDayDelivery' && buttonEnabled
			? 'fulfillment-sameday-filled'
			: 'fulfillment-sameday-outlined';
	});

	fulfillmentButtonTextSDDynamic = computed(() =>
		this.sameDayDeliveryButtonText().sddAvailable
			? this.getSameDayDeliveryTimeText()
			: this.sameDayDeliveryButtonText().fulfillmentButtonText
	);

	isZipcodeNotSetAndSkuResolved = computed(
		() => !!((this.userZipCode() === 'null' || !this.userZipCode()) && this.skuResolved())
	);

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconFulfillmentSamedayFilled,
			homefieldIconFulfillmentSamedayOutlined
		]);
	}

	/**
	 * @description when the button is clicked and action is dispatched to update shipMode in the fulfillment slice of state
	 */
	setShipMode() {
		this.store.dispatch(setFulfillmentButtonsShipMode({ shipMode: 'sameDayDelivery' }));
	}

	getSameDayDeliveryTimeText(): string {
		if (this.isZipcodeNotSetAndSkuResolved()) {
			return '';
		}

		const d = new Date();
		const hours = d.getHours();
		const minutes = d.getMinutes();
		const timeInMinutes = hours * 60 + minutes;
		const threshold = this.sddUpdatedCutoffEnabled() ? 16 * 60 : 14 * 60; // 4pm or 2pm in minutes
		if (timeInMinutes < threshold) {
			return 'Order now, get it today by 8pm';
		} else {
			return 'Order now, get it tomorrow by 8pm';
		}
	}
}
